import { AnyAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { ApiError, ApiResponse } from '../../../../../base/api';
import { Loading, Messenger } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { isEmptyObject } from '../../../../../base/utils';
import { AuthApiResponse, SignUpParams } from '../../api/interfaces';
import { PasswordRecoveryStep, SignUpStep } from '../../enums';
import { prepareSignUpData } from '../../utils';
import { loginActions } from '../slices/loginSlice';
import { passwordRecoveryActions } from '../slices/passwordRecoverySlice';
import { workerAppActions } from '../../../../redux/slices/workerAppSlice';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { authApi } from '../../api';
import { signUpActions } from '../slices/signUpSlice';
import { quickSignUpActions } from '../slices/quickSignUpSlice';

function* submitSignUpChangeConfirmationValue({ payload: { newValue, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        yield call(authApi.validateSignParams, { phone: newValue, validate: true });
        yield put(signUpActions.setField({ id: 'phone', value: newValue }));
        yield put(workerAppActions.sendConfirmationCode(newValue, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(signUpActions.setErrors(e.errors));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitSignUp() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.workerAppSignUp);

    try {
        const signUpData: SignUpParams = yield prepareSignUpData(fields);
        const response: AuthApiResponse = yield call(authApi.signUp, signUpData);

        if (window.dataLayer) {
            window.dataLayer.push({ event: 'user_created', id: response.userId });
        }

        if (response.redirectTo) {
            window.location.href = response.redirectTo;
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.smsVerificationNeeded && step !== SignUpStep.Confirmation) {
                yield put(signUpActions.setStep(SignUpStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(signUpActions.setErrors(e.errors));
                yield put(signUpActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitLogin() {
    yield put(appActions.setLoading(Loading.App));
    const { fields } = yield select((state: RootState) => state.workerAppLogin);

    try {
        const response: AuthApiResponse = yield call(authApi.login, {
            phone: fields.phone,
            password: fields.password,
        });

        if (window.dataLayer) {
            window.dataLayer.push({ event: 'user_login', id: response.userId });
        }

        if (response.redirectTo) {
            window.location.href = response.redirectTo;
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(loginActions.setErrors(e.errors));
            yield put(loginActions.setShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* recoverPassword() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.workerAppPasswordRecovery);

    try {
        const response: ApiResponse<unknown> = yield call(authApi.passwordReset, {
            phone: fields.phone,
            smsCode: fields.smsCode,
        });

        if (response?.passwordResetToken) {
            yield put(passwordRecoveryActions.setPasswordResetToken(response.passwordResetToken));
            yield put(passwordRecoveryActions.setStep(PasswordRecoveryStep.NewPassword));
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.smsVerificationNeeded && step !== PasswordRecoveryStep.Confirmation) {
                yield put(passwordRecoveryActions.setStep(PasswordRecoveryStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(passwordRecoveryActions.setErrors(e.errors));
                yield put(passwordRecoveryActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* setNewPassword() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, passwordResetToken } = yield select((state: RootState) => state.workerAppPasswordRecovery);

    try {
        const response: ApiResponse<unknown> = yield call(authApi.passwordSetNew, {
            password: fields.password,
            passwordResetToken: passwordResetToken,
        });

        if (response.redirectTo) {
            window.location.href = response.redirectTo;
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(passwordRecoveryActions.setErrors(e.errors));
            yield put(passwordRecoveryActions.setShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitPasswordRecoveryChangeConfirmationValue({
    payload: { newValue, recaptchaToken, callback },
}: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        yield call(authApi.passwordReset, {
            phone: newValue,
        });
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.smsVerificationNeeded) {
                yield put(passwordRecoveryActions.setField({ id: 'phone', value: newValue }));
                yield put(workerAppActions.sendConfirmationCode(newValue, recaptchaToken));
                callback();
                return;
            }

            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(passwordRecoveryActions.setErrors(e.errors));
                yield put(passwordRecoveryActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* validateQuickSignUpParams({ payload: { successCallback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields } = yield select((state: RootState) => state.workerAppQuickSignUp);

    try {
        yield call(authApi.quickSignUp, {
            phone: fields.phone,
            messenger: fields.messenger === Messenger.None ? undefined : fields.messenger,
            validate: true
        });

        successCallback();
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(quickSignUpActions.setErrors(e.errors));
                yield put(quickSignUpActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitQuickSignUp({ payload: { successCallback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields } = yield select((state: RootState) => state.workerAppQuickSignUp);

    try {
        const response: AuthApiResponse = yield call(authApi.quickSignUp, {
            phone: fields.phone,
            messenger: fields.messenger === Messenger.None ? undefined : fields.messenger,
            smsCode: fields.smsCode,
        });

        if (window.dataLayer) {
            window.dataLayer.push({ event: 'user_created', id: response.userId });
        }

        successCallback();
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(quickSignUpActions.setErrors(e.errors));
                yield put(quickSignUpActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitQuickSignUpChangeConfirmationValue({ payload: { newValue, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        yield call(authApi.validateSignParams, { phone: newValue, validate: true });
        yield put(signUpActions.setField({ id: 'phone', value: newValue }));
        yield put(workerAppActions.sendConfirmationCode(newValue, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(signUpActions.setErrors(e.errors));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* authSaga() {
    yield takeLatest(signUpActions.submitChangeConfirmationValue, submitSignUpChangeConfirmationValue);
    yield takeLatest(signUpActions.submitSignUp, submitSignUp);
    yield takeLatest(loginActions.submitLogin, submitLogin);
    yield takeLatest(passwordRecoveryActions.submitRecoverPassword, recoverPassword);
    yield takeLatest(passwordRecoveryActions.submitSetNewPassword, setNewPassword);
    yield takeLatest(
        passwordRecoveryActions.submitChangeConfirmationValue,
        submitPasswordRecoveryChangeConfirmationValue,
    );
    yield takeLatest(quickSignUpActions.submitQuickSignUp, submitQuickSignUp);
    yield takeLatest(quickSignUpActions.validateQuickSignUpParams, validateQuickSignUpParams);
    yield takeLatest(quickSignUpActions.submitChangeConfirmationValue, submitQuickSignUpChangeConfirmationValue);
}
